import React from "react";
import { Link } from "react-router-dom";
import RawHeader from "../components/RawHeader";

const Shell = ({ title, subtitle, children, testid }) => (
  <div data-testid={testid} className="min-h-screen text-white">
    <RawHeader variant="public" />
    <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// {subtitle}</div>
      <h1 className="font-display text-4xl sm:text-5xl mb-10 text-[#d4af37]">{title}</h1>
      <div className="prose prose-invert text-zinc-300 text-sm leading-relaxed space-y-5 [&_h2]:text-[#d4af37] [&_h2]:font-display [&_h2]:text-xl [&_h2]:tracking-wide [&_h2]:mt-10 [&_h2]:mb-3 [&_a]:text-[#d4af37]">
        {children}
      </div>
      <div className="mt-16 pt-8 border-t border-zinc-800">
        <Link to="/" className="btn-raw">← BACK TO SHOP</Link>
      </div>
    </section>
    <InfoFooter />
  </div>
);

export const InfoFooter = () => (
  <footer data-testid="raw-family-footer" className="border-t border-[#d4af37]/25 mt-20 bg-[#0a0a0a]">
    {/* SECTION 1 — About RAW Company */}
    <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-14 border-b border-zinc-800/60">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
        <div className="lg:col-span-2 max-w-2xl">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// PART OF RAW COMPANY TECH</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-4 leading-snug">
            One brand in a family of British software.
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Raw Company Clothing is one brand in the{" "}
            <a
              href="https://rawcompanytech.com"
              target="_blank"
              rel="noopener noreferrer me"
              className="text-[#d4af37] hover:underline"
            >
              RAW COMPANY TECH LIMITED
            </a>{" "}
            family — an independent UK software lab (Companies House No.{" "}
            <span className="font-mono text-zinc-300">17349121</span>, England &amp; Wales, director Lee Raw)
            building apps, games and premium products, all engineered in-house.
            Premium UK streetwear with a signature gold standard.
          </p>
        </div>
        <div className="flex lg:justify-end">
          <a
            href="https://rawcompanytech.com/empire"
            target="_blank"
            rel="noopener noreferrer me"
            data-testid="visit-empire-btn"
            className="inline-flex items-center gap-2 px-6 py-3 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors text-[11px] tracking-[0.3em] uppercase font-bold shadow-[0_0_24px_rgba(212,175,55,0.25)]"
          >
            Visit the whole empire →
          </a>
        </div>
      </div>
    </section>

    {/* SECTION 2 — Family links + shop navigation */}
    <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-12">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-[11px] tracking-[0.2em] uppercase">
        <div className="col-span-2 md:col-span-1">
          <div className="text-[#d4af37] mb-3">More from RAW Company</div>
          <ul className="space-y-2 text-zinc-400 normal-case tracking-normal text-xs" data-testid="family-links">
            {[
              { name: "RAW Company Tech", tag: "the hub", href: "https://rawcompanytech.com" },
              { name: "Live Gold Signals", tag: "trading", href: "https://rawcompanytech.com/trading" },
              { name: "Snapdog", tag: "UK dog network", href: "https://dogsnap.co.uk" },
              { name: "Raw Mixes", tag: "DJ mixes", href: "https://rawmixes.uk" },
              { name: "Racing Form Guide", tag: "form data", href: "https://racingformguide.co.uk" },
              { name: "RAW Data", tag: "wealth analytics", href: "https://raw-data.uk" },
            ].map((s) => (
              <li key={s.name}>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="hover:text-white transition-colors"
                >
                  <span className="text-zinc-300">{s.name}</span>
                  <span className="text-zinc-600"> — {s.tag}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[#d4af37] mb-3">Shop</div>
          <ul className="space-y-2 text-zinc-400">
            <li><Link to="/" className="hover:text-white">All Products</Link></li>
            <li><Link to="/?gender=men" className="hover:text-white">Men</Link></li>
            <li><Link to="/?gender=women" className="hover:text-white">Women</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[#d4af37] mb-3">Company</div>
          <ul className="space-y-2 text-zinc-400">
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            <li>
              <a
                href="https://www.instagram.com/raw___clothing/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
              >
                Instagram
              </a>
            </li>
          </ul>
        </div>
        <div>
          <div className="text-[#d4af37] mb-3">Legal</div>
          <ul className="space-y-2 text-zinc-400">
            <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link></li>
            <li><Link to="/returns" className="hover:text-white">Returns &amp; Refunds</Link></li>
            <li><Link to="/shipping" className="hover:text-white">Shipping Info</Link></li>
          </ul>
        </div>
      </div>
    </section>

    {/* SECTION 3 — Company / contact bar */}
    <section data-testid="company-bar" className="border-t border-zinc-800/60 bg-black">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4 text-[11px] text-zinc-500 leading-relaxed">
        <div className="space-y-1.5">
          <div className="text-zinc-300">
            <span className="font-semibold text-white">RAW COMPANY TECH LIMITED</span>
            <span className="text-zinc-600"> · Company No.{" "}
              <a
                href="https://find-and-update.company-information.service.gov.uk/company/17349121"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[#d4af37] hover:underline"
                data-testid="companies-house-link"
                title="Companies House verified"
              >
                17349121
              </a>
            </span>
            <span className="text-zinc-600"> · Registered in England &amp; Wales</span>
          </div>
          <div className="text-zinc-500">
            <a href="mailto:orders@rawcompanytech.com" className="hover:text-[#d4af37]">orders@rawcompanytech.com</a>
            <span className="text-zinc-700"> · </span>
            <a href="tel:+447398238509" className="hover:text-[#d4af37]">07398 238 509</a>
          </div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 pt-1">
            © {new Date().getFullYear()} RAW Company Tech Limited · All Rights Reserved
          </div>
        </div>
        <Link to="/admin/login" className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 hover:text-[#d4af37] shrink-0">
          Admin Login →
        </Link>
      </div>
    </section>
  </footer>
);

export const AboutUs = () => (
  <Shell testid="about-page" subtitle="Our Story" title="About Raw Company">
    <p>Raw Company Clothing is a UK-based luxury apparel brand. Every piece is designed in-house using a tech-pack engine — meaning the full factory specification (dimensions, materials, stitching, branding) lives alongside each design, so nothing is lost in translation between designer and factory.</p>

    {/* Brand Heritage strip — explains the parent ↔ clothing brand relationship */}
    <div data-testid="brand-heritage" className="not-prose my-10 border border-[#d4af37]/40 bg-[#050505] p-6 sm:p-8">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-5">// BRAND HERITAGE</div>
      <h2 className="font-display text-xl sm:text-2xl text-[#d4af37] mb-6">An umbrella, two marks, one family.</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800">
        <div className="bg-black p-6 flex items-start gap-5">
          <img
            src="/raw-lion-transparent.png?v=4"
            alt="RAW Company group lion mark"
            className="w-20 h-20 object-contain shrink-0 opacity-90"
            loading="lazy"
          />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">// PARENT GROUP</div>
            <div className="font-display text-base text-white mb-2">RAW Company LTD</div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              The lion mark belongs to our parent group — the holding company that umbrellas every RAW family business.
              Watch for it on <a href="https://rawcompany.uk" target="_blank" rel="noopener noreferrer" className="text-[#d4af37] hover:underline">rawcompany.uk</a>.
            </p>
          </div>
        </div>
        <div className="bg-black p-6 flex items-start gap-5">
          <img
            src="/icon-512.png?v=4"
            alt="Raw Company Clothing tailor mark"
            className="w-20 h-20 object-contain shrink-0"
            loading="lazy"
          />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-1">// THIS HOUSE</div>
            <div className="font-display text-base text-white mb-2">Raw Company Clothing</div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              The gold tailor figure is the dedicated mark for the clothing line — heritage, craftsmanship, suits-and-stitching.
              Every garment leaves the factory with this mark woven into the inside back collar.
            </p>
          </div>
        </div>
      </div>
      <p className="text-[11px] tracking-[0.15em] uppercase text-zinc-500 mt-5 leading-relaxed">
        ── A promotion, not a switch. The clothing line earned its own identity in 2026, distinct from the holding company that built it.
      </p>
    </div>

    {/* Our other apps — SEO product-family strip */}
    <div data-testid="sister-apps-strip" className="not-prose my-10 border border-zinc-800 bg-[#050505] p-6 sm:p-8">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// OUR OTHER APPS</div>
      <h2 className="font-display text-xl sm:text-2xl text-white mb-1">The RAW COMPANY TECH family</h2>
      <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
        This clothing brand is one product in a wider suite built by{" "}
        <a href="https://rawcompanytech.com" target="_blank" rel="noopener noreferrer me" className="text-[#d4af37] hover:underline">
          RAW COMPANY TECH LIMITED
        </a>.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[
          { name: "RAW Company Tech", tag: "Parent hub", href: "https://rawcompanytech.com" },
          { name: "Racing Form Guide", tag: "Data & tips", href: "https://racingformguide.co.uk" },
          { name: "RAW Mixes", tag: "Audio", href: "https://rawmixes.uk" },
          { name: "RAW Data", tag: "Enterprise", href: "https://raw-data.uk" },
          { name: "DogSnap", tag: "Pet AI", href: "https://dogsnap.co.uk" },
        ].map((app) => {
          const domain = app.href.replace(/^https?:\/\//, "");
          return (
            <a
              key={app.name}
              href={app.href}
              target="_blank"
              rel="noopener noreferrer me"
              data-testid={`sister-app-${domain}`}
              className="group border border-zinc-800 hover:border-[#d4af37] bg-black p-4 flex items-center gap-3 transition-colors"
            >
              <img
                src={`${app.href}/favicon.ico`}
                alt=""
                className="w-8 h-8 object-contain shrink-0 opacity-80 group-hover:opacity-100"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] tracking-[0.15em] uppercase text-white leading-tight">{app.name}</div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 mt-1 truncate">
                  {app.tag}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>

    <h2>Our Philosophy</h2>
    <p>Factory-direct. Tech-pack transparent. Raw-cut construction. We reject disposable fast-fashion in favour of small, deliberate drops. Every Raw Company garment is:</p>
    <ul className="list-disc pl-6 space-y-1">
      <li>Engineered to industry spec — not copied from a trend board</li>
      <li>Branded only with stitched or jacquard-woven gold — no prints, no iron-on, no heat-transfer</li>
      <li>Produced in verified factories from full tech-pack documentation</li>
      <li>Released in limited runs, never restocked after sell-out</li>
    </ul>
    <h2>The Tailor Mark</h2>
    <p>Every Raw Company piece carries the gold tailor mark embroidered in metallic thread (Madeira Classic No.40, shade <span className="font-mono text-[#d4af37]">#D4AF37</span>) on a woven black satin label sewn into the inside back collar — never on the outside. The outside of every garment is reserved entirely for the design itself. Our signature lives where it belongs: stitched in, not stamped on.</p>
    <h2>Get in touch</h2>
    <p>Questions, collaborations, or wholesale enquiries? <Link to="/contact">Contact us here.</Link></p>
  </Shell>
);

export const PrivacyPolicy = () => (
  <Shell testid="privacy-page" subtitle="Last updated: Feb 2026" title="Privacy Policy">
    <p>Raw Company Clothing ("we", "us", "our") respects your privacy and is committed to protecting your personal data. This policy explains how we collect, use, and safeguard information when you use <a href="https://rawcompanyclothing.com">rawcompanyclothing.com</a>.</p>

    <h2>1. Data we collect</h2>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>Order data</strong> — name, shipping &amp; billing address, phone, email, order contents (collected by Stripe during checkout)</li>
      <li><strong>Payment data</strong> — processed directly by Stripe; we never see or store your card details</li>
      <li><strong>Technical data</strong> — IP address, browser type, pages viewed (standard analytics)</li>
    </ul>

    <h2>2. How we use your data</h2>
    <ul className="list-disc pl-6 space-y-1">
      <li>To fulfil and ship your order</li>
      <li>To send order confirmations, shipping updates and receipts</li>
      <li>To comply with UK tax and accounting obligations (HMRC VAT records, retained 6 years)</li>
      <li>To improve the site and prevent fraud</li>
    </ul>

    <h2>3. Sharing</h2>
    <p>We share data only with processors necessary to run the shop: Stripe (payments), our shipping carrier (delivery), and our email provider (order notifications). We never sell your data to third parties.</p>

    <h2>4. Your rights (UK GDPR)</h2>
    <p>You have the right to access, correct, delete, or port your personal data, and to withdraw consent. Email <a href="mailto:hello@rawcompanyclothing.com">hello@rawcompanyclothing.com</a> to exercise any of these rights.</p>

    <h2>5. Cookies</h2>
    <p>We use essential cookies for your shopping session and anonymous analytics cookies to understand site traffic. No advertising cookies are set.</p>

    <h2>6. Data retention</h2>
    <p>Order and invoice records are retained for 6 years to meet HMRC requirements. Account data is deleted on request unless required by law.</p>

    <h2>7. Contact</h2>
    <p>For any privacy questions: <a href="mailto:hello@rawcompanyclothing.com">hello@rawcompanyclothing.com</a>.</p>
  </Shell>
);

export const TermsConditions = () => (
  <Shell testid="terms-page" subtitle="Last updated: Feb 2026" title="Terms & Conditions">
    <p>These terms govern your use of <a href="https://rawcompanyclothing.com">rawcompanyclothing.com</a> and any purchase you make from us. By placing an order you agree to them.</p>

    <h2>1. About us</h2>
    <p>Raw Company Clothing is a trading name based in the United Kingdom. Contact: <a href="mailto:hello@rawcompanyclothing.com">hello@rawcompanyclothing.com</a>.</p>

    <h2>2. Ordering</h2>
    <p>All orders are subject to availability and acceptance. A contract is formed when we send you an order confirmation email. Prices include UK VAT at 20% where applicable. Shipping is added at checkout.</p>

    <h2>3. Payment</h2>
    <p>Payments are processed securely by Stripe. We accept major debit and credit cards. Payment must be cleared before dispatch.</p>

    <h2>4. Delivery</h2>
    <p>We aim to dispatch within 3 working days. Delivery estimates are indicative. Risk passes to you on delivery.</p>

    <h2>5. Limited runs</h2>
    <p>Our products are produced in limited runs. Once a size sells out it may not be restocked. We reserve the right to cancel an order if an item is unexpectedly unavailable, in which case you'll receive a full refund.</p>

    <h2>6. Returns &amp; refunds</h2>
    <p>See our <Link to="/returns">Returns &amp; Refunds</Link> policy. UK consumers have a 14-day right to cancel under the Consumer Contracts Regulations 2013.</p>

    <h2>7. Liability</h2>
    <p>Our liability to you is limited to the price paid for the goods. We do not exclude liability for death, personal injury caused by our negligence, fraud, or any other liability that cannot be excluded by law.</p>

    <h2>8. Governing law</h2>
    <p>These terms are governed by the laws of England and Wales, and subject to the exclusive jurisdiction of the English courts.</p>
  </Shell>
);

export const Returns = () => (
  <Shell testid="returns-page" subtitle="Consumer rights & returns" title="Returns & Refunds">
    <h2>14-day right to cancel</h2>
    <p>If you change your mind, you can return any unworn item in its original condition within 14 days of receipt for a full refund. Just email <a href="mailto:returns@rawcompanyclothing.com">returns@rawcompanyclothing.com</a> with your order number.</p>

    <h2>How to return</h2>
    <ol className="list-decimal pl-6 space-y-1">
      <li>Email us within 14 days of delivery to request a return</li>
      <li>Pack the item in its original packaging, unworn, with all tags attached</li>
      <li>We'll send you a return address; postage is at your cost unless the item is faulty</li>
      <li>Refund is issued within 7 working days of receiving the item back</li>
    </ol>

    <h2>Faulty items</h2>
    <p>If your item arrives faulty or is not as described, contact us within 30 days — we'll cover return postage and issue a full refund or replacement.</p>

    <h2>Non-returnable</h2>
    <ul className="list-disc pl-6 space-y-1">
      <li>Items that have been worn, washed, altered, or damaged after receipt</li>
      <li>Underwear or swimwear for hygiene reasons</li>
    </ul>

    <h2>Refund method</h2>
    <p>Refunds are issued to the original payment method via Stripe. Bank processing times can add 3–5 working days after we issue the refund.</p>
  </Shell>
);

export const Shipping = () => (
  <Shell testid="shipping-page" subtitle="Delivery & tracking" title="Shipping Information">
    <h2>UK Delivery</h2>
    <p>Standard UK shipping: <strong>£4.95</strong>, 2–4 working days. Express UK: <strong>£8.95</strong>, 1–2 working days (order before 2pm).</p>

    <h2>International</h2>
    <p>European: <strong>£14.95</strong>, 5–10 working days. Rest of world: <strong>£24.95</strong>, 7–21 working days. Import duties may apply — these are the customer's responsibility.</p>

    <h2>Tracking</h2>
    <p>Every order is dispatched with a tracked service. You'll receive a tracking link by email once your order ships.</p>

    <h2>Dispatch time</h2>
    <p>We aim to dispatch within 3 working days of order confirmation. Limited drops may take slightly longer — we'll always email you if there's a delay.</p>
  </Shell>
);

export const Contact = () => (
  <Shell testid="contact-page" subtitle="Get in touch" title="Contact">
    <p>We reply to every email personally — usually within 24 hours on working days.</p>

    <h2>General enquiries</h2>
    <p><a href="mailto:hello@rawcompanyclothing.com">hello@rawcompanyclothing.com</a></p>

    <h2>Orders &amp; support</h2>
    <p><a href="mailto:support@rawcompanyclothing.com">support@rawcompanyclothing.com</a></p>

    <h2>Returns</h2>
    <p><a href="mailto:returns@rawcompanyclothing.com">returns@rawcompanyclothing.com</a></p>

    <h2>Social</h2>
    <p>Instagram: <a href="https://www.instagram.com/raw___clothing/" target="_blank" rel="noopener noreferrer">@raw___clothing</a></p>

    <h2>Wholesale &amp; press</h2>
    <p><a href="mailto:hello@rawcompanyclothing.com">hello@rawcompanyclothing.com</a></p>
  </Shell>
);
